import { useMemo, useRef, useState } from 'react';

import { formatCount } from '@/lib/formatting';

import styles from './SampleBrowser.module.css';

export interface SampleBrowserProps {
  samples: Int16Array;
  perRow?: number;
}

const ROW_HEIGHT = 22;
const OVERSCAN = 8;

/**
 * The decoded samples, browsable.
 *
 * The Flutter app renders ten samples per line in a lazy `ListView`; this is
 * the same idea with a windowed list, because a session of 400,000 samples is
 * 40,000 rows and the DOM will not hold them. Only the rows in view are
 * mounted, so scrolling stays smooth at any session length.
 */
export function SampleBrowser({ samples, perRow = 10 }: SampleBrowserProps) {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(320);
  const [jump, setJump] = useState('');

  const rowCount = Math.ceil(samples.length / perRow);
  const indexWidth = String(samples.length).length;
  const valueWidth = useMemo(() => {
    // Widest rendered number, so the columns line up without measuring text.
    let widest = 1;
    for (let i = 0; i < samples.length; i += Math.max(1, Math.floor(samples.length / 5000))) {
      widest = Math.max(widest, String(samples[i]).length);
    }
    return Math.max(widest, 6);
  }, [samples]);

  const firstRow = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN);
  const lastRow = Math.min(
    rowCount,
    Math.ceil((scrollTop + viewportHeight) / ROW_HEIGHT) + OVERSCAN,
  );

  const rows = [];
  for (let row = firstRow; row < lastRow; row += 1) {
    const start = row * perRow;
    const end = Math.min(start + perRow, samples.length);
    const parts: string[] = [];
    for (let i = start; i < end; i += 1) {
      parts.push(String(samples[i]).padStart(valueWidth));
    }
    rows.push(
      <div className={styles.row} key={row}>
        <span className={styles.index} style={{ width: `${indexWidth}ch` }}>
          {String(start).padStart(indexWidth)}
        </span>
        <span className={styles.values}>{parts.join(' ')}</span>
      </div>,
    );
  }

  const onJump = () => {
    const target = Number.parseInt(jump.replace(/[^\d]/g, ''), 10);
    if (Number.isNaN(target)) return;
    const clamped = Math.max(0, Math.min(samples.length - 1, target));
    const top = Math.floor(clamped / perRow) * ROW_HEIGHT;
    viewportRef.current?.scrollTo({ top, behavior: 'smooth' });
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.toolbar}>
        <span>
          {formatCount(samples.length)} samples · {perRow} per row
        </span>
        <span className={styles.jump}>
          <label htmlFor="ela-sample-jump">Go to sample</label>
          <input
            id="ela-sample-jump"
            className={styles.jumpInput}
            inputMode="numeric"
            placeholder="0"
            value={jump}
            onChange={(event) => setJump(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') onJump();
            }}
            onBlur={onJump}
          />
        </span>
      </div>

      <div
        ref={(element) => {
          viewportRef.current = element;
          if (element) setViewportHeight(element.clientHeight);
        }}
        className={styles.viewport}
        onScroll={(event) => setScrollTop(event.currentTarget.scrollTop)}
      >
        <div className={styles.spacer} style={{ height: rowCount * ROW_HEIGHT }}>
          <div className={styles.rows} style={{ transform: `translateY(${firstRow * ROW_HEIGHT}px)` }}>
            {rows}
          </div>
        </div>
      </div>
    </div>
  );
}
